import type { Degrees, Millimeters, Point2D, Point3D } from '../model/types.js';
import type { PinDef } from '../library/component-def.js';
import type { PinState } from '../model/pin.js';

/**
 * Trayectoria 2D de un pin en coordenadas LOCALES al componente.
 *
 * Modelo simplificado, plano:
 *   - Solo se aplica α (rotación en el plano del dibujo).
 *   - Cada bend suma su α al azimut acumulado. Los segmentos posteriores
 *     heredan la orientación resultante hasta que otro bend la modifique.
 *   - β (out-of-plane) se ignora en el render. El modelo lo conserva
 *     para futuras iteraciones; hoy no hay Z, todo Z = 0.
 *
 * Los solapamientos entre pines o componentes se resuelven con Z-order
 * (mover al frente / al fondo), no con perspectiva 3D.
 */

const DEG2RAD = Math.PI / 180;

type Vec3 = { x: number; y: number; z: number };

function v3(x: number, y: number, z: number): Vec3 {
  return { x, y, z };
}

function addScaled(p: Vec3, dir: Vec3, len: number): Vec3 {
  return { x: p.x + dir.x * len, y: p.y + dir.y * len, z: p.z + dir.z * len };
}

export interface PinPathVertex {
  position: Point3D;
  forward: Point3D;
  bend_index: number | 'attachment' | 'tip';
}

/**
 * Longitud del "paso" de un segmento del pin en función de su dirección:
 *  - Direcciones axiales (0°, 90°, 180°, 270°): un paso de rejilla.
 *  - Direcciones diagonales (45°, 135°, 225°, 315°): √2 pasos de
 *    rejilla, exactamente lo que separa dos nodos de rejilla en
 *    diagonal.
 * Como todos los bends son múltiplos de 45°, cada vértice del pin
 * cae siempre en un nodo de la rejilla (asumiendo attachment
 * grid-alineado).
 */
const GRID_STEP: Millimeters = 2.54;
const DIAG_STEP: Millimeters = GRID_STEP * Math.SQRT2;

function segmentLengthFor(azimuthRad: number): Millimeters {
  const deg = (((azimuthRad * 180 / Math.PI) % 360) + 360) % 360;
  // Redondea al múltiplo de 45° más cercano y decide axial vs diagonal.
  const snapped = Math.round(deg / 45) * 45;
  const isDiag = Math.abs(snapped % 90) > 1;
  return isDiag ? DIAG_STEP : GRID_STEP;
}

export function computePinPath3D(def: PinDef, state: PinState): PinPathVertex[] {
  let azimuthRad = def.direction_deg * DEG2RAD;
  let forward: Vec3 = v3(Math.cos(azimuthRad), Math.sin(azimuthRad), 0);
  let position: Vec3 = v3(def.attachment.x, def.attachment.y, 0);

  const vertices: PinPathVertex[] = [
    {
      position: { ...position },
      forward: { ...forward },
      bend_index: 'attachment',
    },
  ];

  const cut = state.cut_at_index;
  const lastBendIndexIncluded = cut === null ? def.bend_points_count - 1 : cut;

  for (let i = 0; i <= lastBendIndexIncluded; i++) {
    const segLen = segmentLengthFor(azimuthRad);
    position = addScaled(position, forward, segLen);

    const bend = state.bends[i];
    if (bend !== undefined) {
      const alphaDeg = bend.angle_in_plane_deg;
      azimuthRad += alphaDeg * DEG2RAD;
      forward = v3(Math.cos(azimuthRad), Math.sin(azimuthRad), 0);
    }

    vertices.push({
      position: { ...position },
      forward: { ...forward },
      bend_index: i,
    });
  }

  if (cut === null) {
    const segLen = segmentLengthFor(azimuthRad);
    position = addScaled(position, forward, segLen);
    vertices.push({
      position: { ...position },
      forward: { ...forward },
      bend_index: 'tip',
    });
  }

  return vertices;
}

/**
 * Proyecta un punto local a 2D del lienzo aplicando posición y rotación
 * del cuerpo del componente. La rotación es siempre múltiplo de 90°.
 */
export function projectLocalToScene(
  local: Point3D,
  componentPos: Point2D,
  componentRotDeg: Degrees,
  flipped: boolean = false,
): Point2D {
  const y = flipped ? -local.y : local.y;
  const a = componentRotDeg * DEG2RAD;
  const c = Math.cos(a);
  const s = Math.sin(a);
  return {
    x: componentPos.x + local.x * c - y * s,
    y: componentPos.y + local.x * s + y * c,
  };
}
